import { useCallback, useRef } from "react";
import { normalizeTimestampMs } from "@/lib/lytick/replay-utils";
import { useChartStore } from "@/stores/chartStore";
import { useReplayStore } from "@/stores/replayStore";

const MAX_REPLAY_RANGE_MS = 365 * 24 * 60 * 60 * 1000;

export function useReplayPreflight() {
  const replayStore = useReplayStore();
  const activePreflightTokenRef = useRef(0);

  const cancelPreflight = useCallback(() => {
    activePreflightTokenRef.current += 1;
    useReplayStore.getState().cancelPreflight();
  }, []);

  const startPreflight = useCallback(
    async (startDate: number, endDate: number) => {
      const token = ++activePreflightTokenRef.current;
      const replay = useReplayStore.getState();
      const chart = useChartStore.getState();
      const startMs = normalizeTimestampMs(startDate);
      const endMs = normalizeTimestampMs(endDate);
      const nowMs = Date.now();

      replay.startPreflight(startDate, endDate);

      if (startMs === null || endMs === null || startMs >= endMs) {
        replay.setErrorMessage("Start date must be before end date.");
        return;
      }

      if (startMs > nowMs || endMs > nowMs) {
        replay.setErrorMessage("Replay dates cannot be in the future.");
        return;
      }

      if (endMs - startMs > MAX_REPLAY_RANGE_MS) {
        replay.setErrorMessage("Replay range cannot exceed one year.");
        return;
      }

      // We trigger the backfiller by setting state to "loading";
      // the useReplayBackfill hook takes it from here!
      // Always fetches 1m data — aggregation to higher timeframes happens at display time.
      replay.setLoadProgress(0);
    },
    [],
  );

  const restartPreflight = useCallback(async () => {
    const state = useReplayStore.getState();
    if (state.startDate === null || state.endDate === null) {
      return;
    }

    await startPreflight(state.startDate, state.endDate);
  }, [startPreflight]);

  return {
    cancelPreflight,
    restartPreflight,
    startPreflight,
    isLoading: replayStore.sessionState === "loading",
  };
}
